import { subscriptionApi } from './apiUtils';

// Feature display names for the `not allowed` reason strings shown in UI
// tooltips / disabled buttons.
const FEATURE_LABEL = {
	'save-paper': 'Saved papers',
	'save-topic': 'Followed topics',
	'ai-summary': 'AI summaries',
	'ai-chat': 'AI chat'
};

// Parse a Stripe-shaped quantity metadata value into a number. Stripe returns
// metadata values as strings, and we treat "" / null / undefined as
// "unlimited" (to match the server's Infinity convention) while preserving
// numeric zero as "not included in plan".
const parseQuota = (raw) => {
	if (raw === undefined || raw === null || raw === '') return Infinity;
	const parsed = Number(raw);
	if (!Number.isFinite(parsed)) return Infinity;
	return parsed;
};

// Match by substring because the Stripe catalog provisions features per plan
// with lookup_keys shaped as `<base>-<plan>` (e.g. `ai-chat-pro-plan`). A
// customer only has one active entitlement per base feature, so substring
// matching is unambiguous. Mirrors backend eligibilityService.fetchQuotaFromStripe.
const findFeatureQuota = (entitlements, lookupKey) => {
	const match = entitlements.find(
		(item) =>
			typeof item?.feature?.lookup_key === 'string' &&
			item.feature.lookup_key.includes(lookupKey)
	);
	if (!match) return 0;
	return parseQuota(match.feature?.metadata?.quantity);
};

export const getEligibility = async (user) => {
	try {
		// Every signed-in user should see at least the free-plan floor. If the
		// Stripe sync is still catching up we won't have a tier yet — the
		// server's /active-entitlements endpoint already treats null/unknown
		// tiers as free_plan, so we just fetch and render the result.
		const { activeEntitlements } = await subscriptionApi.getActiveEntitlements();
		const data = activeEntitlements?.data || [];

		const favoritePaperQuota = findFeatureQuota(data, 'save-paper');
		const favoriteTagQuota = findFeatureQuota(data, 'save-topic');
		const aiSummaryQuota = findFeatureQuota(data, 'ai-summary');
		const aiChatQuota = findFeatureQuota(data, 'ai-chat');

		const favoritePaperUsage = user.favoritePapers?.length || 0;
		const favoriteTagUsage = user.favoriteTags?.length || 0;
		const aiSummaryUsage = user.aiSummarizedPapers?.length || 0;
		const aiChatUsage = user.aiChattedPapers?.length || 0;

		// The user is considered to have an active subscription any time we
		// know their tier — including free_plan. This flag is consumed by UI
		// that wants to show "subscription active" vs "no plan yet" state.
		const isSubscriptionActive = !!user.stripeSubscriptionTier;

		const checkFeatureEligibility = (quota, usage, lookupKey) => {
			const label = FEATURE_LABEL[lookupKey] || lookupKey;

			if (quota === 0) {
				return {
					allowed: false,
					reason: `${label} not included in your plan`
				};
			}

			if (!Number.isFinite(quota)) {
				return { allowed: true, reason: '' };
			}

			if (usage >= quota) {
				return {
					allowed: false,
					reason: `${label} quota (${quota}) reached`
				};
			}

			return { allowed: true, reason: '' };
		};

		const favoritePaperStatus = checkFeatureEligibility(
			favoritePaperQuota,
			favoritePaperUsage,
			'save-paper'
		);
		const favoriteTagStatus = checkFeatureEligibility(
			favoriteTagQuota,
			favoriteTagUsage,
			'save-topic'
		);
		const aiSummaryStatus = checkFeatureEligibility(
			aiSummaryQuota,
			aiSummaryUsage,
			'ai-summary'
		);
		const aiChatStatus = checkFeatureEligibility(
			aiChatQuota,
			aiChatUsage,
			'ai-chat'
		);

		return {
			// Quotas
			favoritePaperQuota,
			favoriteTagQuota,
			aiSummaryQuota,
			aiChatQuota,

			// Usage
			favoritePaperUsage,
			favoriteTagUsage,
			aiSummaryUsage,
			aiChatUsage,

			// Subscription status
			isSubscriptionActive,

			// Feature eligibility
			favoritePaperAllowed: favoritePaperStatus.allowed,
			favoriteTagAllowed: favoriteTagStatus.allowed,
			aiSummaryAllowed: aiSummaryStatus.allowed,
			aiChatAllowed: aiChatStatus.allowed,

			// Reason messages
			favoritePaperNotAllowedReason: favoritePaperStatus.reason,
			favoriteTagNotAllowedReason: favoriteTagStatus.reason,
			aiSummaryNotAllowedReason: aiSummaryStatus.reason,
			aiChatNotAllowedReason: aiChatStatus.reason
		};
	} catch (error) {
		console.error('Error fetching active eligibility:', error);
		// Restrictive defaults on error — better to disable features than to
		// let the client silently allow something the server will reject.
		return {
			favoritePaperQuota: 0,
			favoriteTagQuota: 0,
			aiSummaryQuota: 0,
			aiChatQuota: 0,
			favoritePaperUsage: 0,
			favoriteTagUsage: 0,
			aiSummaryUsage: 0,
			aiChatUsage: 0,
			isSubscriptionActive: false,
			favoritePaperAllowed: false,
			favoriteTagAllowed: false,
			aiSummaryAllowed: false,
			aiChatAllowed: false,
			favoritePaperNotAllowedReason: 'Error checking eligibility',
			favoriteTagNotAllowedReason: 'Error checking eligibility',
			aiSummaryNotAllowedReason: 'Error checking eligibility',
			aiChatNotAllowedReason: 'Error checking eligibility'
		};
	}
};
